import Select from "@layouts/betto/authPopup/components/Select/index.js";
import { AuthCloseIcon } from "@layouts/betto/authPopup/icons/AuthCloseIcon.jsx";
import { Form } from "antd";
import PropTypes from "prop-types";
import React from "react";
import { createPortal } from "react-dom";
import { getActiveLanguage } from "react-localize-redux";
import { useSelector } from "react-redux";

import {
	BettoShareModalActions,
	BettoShareModalCard,
	BettoShareModalCloseButton,
	BettoShareModalContent,
	BettoShareModalCopy,
	BettoShareModalField,
	BettoShareModalOverlay,
	BettoShareModalPrimaryButton,
	BettoShareModalSecondaryButton,
	BettoShareModalTitle,
} from "./BettoShareChannelModal.styles";

export const BettoShareChannelModal = ({
	isOpen,
	translate,
	titleKey = "chat.shareModal.title",
	channels,
	selectedChannelId,
	onChannelChange,
	isSubmitting,
	canSubmit,
	onClose,
	onSubmit,
}) => {
	const currentLanguage = useSelector((state) => getActiveLanguage(state.localize)?.code || "en");

	if (!isOpen) {
		return null;
	}

	const hasChannels = channels.length > 0;
	const locale = currentLanguage === "tr" ? "tr-TR" : "en-US";

	return createPortal(
		<BettoShareModalOverlay
			role="presentation"
			onClick={onClose}
			data-testid="chat-share-modal-overlay">
			<BettoShareModalCard
				role="dialog"
				aria-modal="true"
				onClick={(event) => event.stopPropagation()}
				data-id="betto-share-channel-modal">
				<BettoShareModalCloseButton
					type="button"
					data-testid="button-alert-close"
					onClick={onClose}
					aria-label="Close">
					<AuthCloseIcon />
				</BettoShareModalCloseButton>

				<BettoShareModalContent>
					<BettoShareModalCopy>
						<BettoShareModalTitle data-testid="chat-share-modal-title">
							{translate(titleKey)}
						</BettoShareModalTitle>
					</BettoShareModalCopy>

					{hasChannels && (
						<BettoShareModalField>
							<Form.Item className="auth-form-item select">
								<Select
									value={selectedChannelId || undefined}
									onChange={onChannelChange}
									showSearch
									autocomplete="none"
									optionFilterProp="children"
									selectPlaceholder={translate("chat.selectChannel")}
									getPopupContainer={() => document.body}
									dropdownMatchSelectWidth
									dropdownStyle={{ zIndex: 1301 }}
									filterOption={(input, option) =>
										String(option.props.children)
											.toLowerCase()
											.indexOf(input.toLowerCase()) >= 0 ||
										String(option.props.value)
											.toLowerCase()
											.indexOf(input.toLowerCase()) >= 0
									}
									data-testid="chat-share-channel-select">
									{channels.map((channel) => (
										<Select.Option
											key={channel.channelId}
											value={channel.channelId}>
											{channel.label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</BettoShareModalField>
					)}

					<BettoShareModalActions>
						<BettoShareModalSecondaryButton
							type="button"
							data-testid="chat-share-cancel"
							onClick={onClose}>
							{translate("chat.tipModal.cancel").toLocaleUpperCase(locale)}
						</BettoShareModalSecondaryButton>
						<BettoShareModalPrimaryButton
							type="button"
							disabled={!canSubmit || isSubmitting}
							data-testid="chat-share-submit"
							onClick={onSubmit}>
							{translate("chat.shareModal.send").toLocaleUpperCase(locale)}
						</BettoShareModalPrimaryButton>
					</BettoShareModalActions>
				</BettoShareModalContent>
			</BettoShareModalCard>
		</BettoShareModalOverlay>,
		document.body
	);
};

BettoShareChannelModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	translate: PropTypes.func.isRequired,
	titleKey: PropTypes.string,
	channels: PropTypes.arrayOf(
		PropTypes.shape({
			channelId: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
		})
	).isRequired,
	selectedChannelId: PropTypes.string.isRequired,
	onChannelChange: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
	canSubmit: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
};

export default BettoShareChannelModal;
